<template>
	<div class="task-users">
		<UserAssignSelect
			v-if="taskData && taskData.assignees !== undefined"
			:size
			:user-ids="userIds"
			:assigned-roles="assignedRoles"
			:preloaded-users="assignedUsers"
			:owner="taskData.responsibleUser"
			:owner-title="$t('task.owner')"
			:title="$t('assign_to')"
			:sub-group-titles="subGroupTitles"
			:is-owner-excluded-from-selection="false"
			:task-id="taskData.id"
			:project-id="taskData.project?.id"
			load-project-members
			:max-avatars-shown-without-hiding="MAX_AVATARS_SHOWN"
			:is-dropdown-enabled="taskData.permissions.canModify"
			:is-modify-enabled="taskData.permissions.canModify"
			:is-owner-enabled="useTaskOwnerSetting && isOwnerVisible"
			:is-single-user-assign="!useTaskToMultipleUsersSetting"
			:has-individual-time-allocation="hasIndividualTimeAllocation"
			:allocated-times="allocatedTimes"
			:confirmed-allocated-times="confirmedAllocatedTimes"
			:has-custom-allocation="
				adjustedHasCustomAllocation ?? taskData.hasCustomAllocation
			"
			:no-members-text="$t('unassigned')"
			:planned-duration="taskData.durationPlanned"
			is-grouped-by-roles
			:filter-by-role-id="roleId"
			:enable-loading="false"
			enable-shortcuts
			:enable-role-selection="assignRolesToTasksSetting"
			@update:owner="onUpdateOwner"
			@update:assignees="onUpdateAssignees"
			@update:has-custom-allocation="onUpdateHasCustomAllocation"
			@update:allocated-times="onUpdateAllocatedTime"
			@role-replaced-with-user="trackRoleReplacement"
		/>

		<UserAssignSelectContainerSkeleton
			v-else
			:size
			:is-owner-enabled="isOwnerVisible"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import { Setting } from '@/constants/settings';
import {
	type Role,
	type Task,
	type TaskUsersTaskFragment,
	type TaskListEnrichmentFieldsFragment,
	type UserAssignSelectUserFragment,
	type User,
	useTaskUsersAssigneesSaveMutation,
	useTaskUsersOwnerSaveMutation,
	useTaskUsersQuery,
} from '@/generated/graphql';
import { useRefetchOnSocketMessage } from '@/lib/apollo/apolloTools';
import { SocketMessageType } from '@/constants/socketMessages';
import {
	useBooleanSetting,
	useSetting,
} from '@/composables/settings/useSettings';
import {
	UserAssignSelect,
	UserAssignSelectContainerSkeleton,
	type UserAssignSelectAssigneesPayload,
} from '@/features/User';
import {
	type TaskAssignees,
	type TaskAssigneeInputDuration,
	type TaskAssigneeAllocatedDuration,
} from './types';
import { timeToSeconds } from '@/utils/time';
import { UserAssignSelectSize } from '@/features/User/constants';
import { type UserAssignSelectRole } from '@/features/User/types';
import { type ValueOf } from '@/types/utils';
import { useTranslation } from '@/composables';
import {
	trackRoleAssigneeChanges,
	trackRoleReplacement,
} from '@/features/Task/utils';

const MAX_AVATARS_SHOWN = 3;

export type TaskUsersProps = {
	taskId: Task['id'];
	roleId?: Role['id'];
	size?: ValueOf<typeof UserAssignSelectSize>;
	isOwnerVisible?: boolean;
	taskEnrichmentData?:
		| TaskListEnrichmentFieldsFragment
		| TaskUsersTaskFragment
		| null;
};

const props = withDefaults(defineProps<TaskUsersProps>(), {
	roleId: undefined,
	size: UserAssignSelectSize.LARGE,
	isOwnerVisible: true,
	taskEnrichmentData: undefined,
});

const { t } = useTranslation();
const { mutate: saveOwner } = useTaskUsersOwnerSaveMutation({});
const { mutate: saveAssignees } = useTaskUsersAssigneesSaveMutation({});

const allocatedTimes = ref<TaskAssigneeAllocatedDuration>({
	users: new Map(),
	roles: new Map(),
});
const adjustedHasCustomAllocation = ref<Task['hasCustomAllocation']>();

const [useTaskOwnerSetting] = useBooleanSetting(Setting.USE_TASK_OWNER);
const [useIndividualTimeAllocation] = useSetting(
	Setting.INDIVIDUAL_TIME_ALLOCATION
);
const [useTaskToMultipleUsersSetting] = useBooleanSetting(
	Setting.USE_TASK_TO_MULTIPLE_USERS
);
const [assignRolesToTasksSetting] = useBooleanSetting(
	Setting.ASSIGN_ROLES_TO_TASKS
);

const { result } = useRefetchOnSocketMessage(
	useTaskUsersQuery(
		{
			id: props.taskId,
		},
		() => ({
			enabled: props.taskEnrichmentData === undefined,
		})
	),
	{
		messages: [SocketMessageType.TASK_UPDATED],
		filter: (message) => {
			if (String(message.data.event_id) !== String(props.taskId)) {
				return false;
			}

			const assignees = result.value?.Task?.assignees || [];
			const currentUserIds: User['id'][] = [];
			const currentRoleIds: Role['id'][] = [];

			for (const { user, role } of assignees) {
				if (user?.id) {
					currentUserIds.push(user.id);
				}
				if (role?.id) {
					currentRoleIds.push(role.id);
				}
			}

			const isAssignedUserValuesEqual = isEqual(
				sortBy(currentUserIds),
				sortBy(message.data.assigned_to)
			);
			const isAssignedRolesValuesEqual = isEqual(
				sortBy(currentRoleIds),
				sortBy(message.data.assigned_roles ?? [])
			);

			return !isAssignedUserValuesEqual || !isAssignedRolesValuesEqual;
		},
	}
);

const taskData = computed(() => {
	return props.taskEnrichmentData ?? result.value?.Task;
});

const hasIndividualTimeAllocation = computed<boolean>(() => {
	return Boolean(
		taskData.value?.parentId === null &&
		useIndividualTimeAllocation.value &&
		useIndividualTimeAllocation.value !== '0'
	);
});

const assigneesData = computed(() => {
	const assignees = taskData.value?.assignees || [];
	const userIds: User['id'][] = [];
	const assignedRoles: UserAssignSelectRole[] = [];
	const assignedUsers: UserAssignSelectUserFragment[] = [];

	for (const { user, role } of assignees) {
		if (user?.id) {
			userIds.push(user.id);
			if ('name' in user && 'status' in user && 'type' in user) {
				assignedUsers.push(user as UserAssignSelectUserFragment);
			}
		}
		if (role?.id) {
			assignedRoles.push(role);
		}
	}

	return { userIds, assignedRoles, assignedUsers };
});

const userIds = computed(() => assigneesData.value.userIds);
const assignedRoles = computed(() => assigneesData.value.assignedRoles);
const assignedUsers = computed(() => assigneesData.value.assignedUsers);

const subGroupTitles = computed(() => {
	return [
		t('project_header_project_members'),
		t('project_header_other_users'),
		t('project_header_cupo_users'),
	];
});

const createAllocatedTimeMaps = (
	assignees: TaskAssignees
): TaskAssigneeAllocatedDuration => {
	const users = new Map<User['id'], number>();
	const roles = new Map<Role['id'], number>();

	for (const assignee of assignees) {
		if (assignee.user?.id) {
			users.set(assignee.user.id, assignee.allocatedDuration);
		}
		if (assignee.role?.id) {
			roles.set(assignee.role.id, assignee.allocatedDuration);
		}
	}

	return { users, roles };
};

const confirmedAllocatedTimes = computed(() => {
	return createAllocatedTimeMaps(taskData.value?.assignees || []);
});

const onUpdateOwner = (
	user: UserAssignSelectUserFragment | undefined | null
) => {
	saveOwner({
		input: {
			taskId: props.taskId,
			responsibleUserId: user?.id,
		},
	});
};

const onUpdateAssignees = async ({
	users,
	shouldNotify,
	roles = [],
}: UserAssignSelectAssigneesPayload) => {
	trackRoleAssigneeChanges({
		roles,
		currentRoleIds: assignedRoles.value.map((role) => role.id),
	});

	for (const userId of allocatedTimes.value.users.keys()) {
		if (!users.some((user) => user.id === userId)) {
			allocatedTimes.value.users.delete(userId);
		}
	}

	for (const roleId of allocatedTimes.value.roles.keys()) {
		if (!roles.some((role) => role.id === roleId)) {
			allocatedTimes.value.roles.delete(roleId);
		}
	}

	const assignedUsers = users.map(({ id }) => ({
		userId: id,
		allocatedDuration: allocatedTimes.value.users.get(id) ?? 0,
	}));

	const assignedRolesInput = roles.map(({ id }) => ({
		roleId: id,
		allocatedDuration: allocatedTimes.value.roles.get(id) ?? 0,
	}));

	saveAssignees({
		input: {
			assignees: [...assignedUsers, ...assignedRolesInput],
			taskId: props.taskId,
			hasCustomAllocation:
				adjustedHasCustomAllocation.value ??
				taskData.value?.hasCustomAllocation,
			shouldNotify,
		},
	});
};

const onUpdateHasCustomAllocation = (hasCustomAllocation: boolean) => {
	adjustedHasCustomAllocation.value = hasCustomAllocation;
};

const onUpdateAllocatedTime = (
	allocatedTimesInput: TaskAssigneeInputDuration
) => {
	allocatedTimes.value.users.clear();
	allocatedTimes.value.roles.clear();

	for (const [userId, allocatedDuration] of Object.entries(
		allocatedTimesInput.users
	)) {
		allocatedTimes.value.users.set(
			userId,
			timeToSeconds(allocatedDuration)
		);
	}

	for (const [roleId, allocatedDuration] of Object.entries(
		allocatedTimesInput.roles
	)) {
		allocatedTimes.value.roles.set(
			roleId,
			timeToSeconds(allocatedDuration)
		);
	}
};

watch(
	taskData,
	(newTask) => {
		allocatedTimes.value = createAllocatedTimeMaps(
			newTask?.assignees || []
		);
	},
	{ immediate: true }
);
</script>
